import { useState } from 'react';
import type {
  AssignableMember,
  Project,
  ProjectStatus,
  SortDirection,
  UpdateProjectInput,
} from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, PROJECT_STATUSES } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { ProjectForecastPanel } from '../forecast/ProjectForecastPanel.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import {
  savedBoolean,
  savedString,
  savedStringArray,
  useFilterDefaults,
} from '../filters/useFilterDefaults.js';
import { ProjectFormModal } from './ProjectFormModal.js';
import { useProjects, useProjectManagers, useUpdateProject } from './api.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { PROJECT_STATUS_LABELS } from './status.js';

/** Column keys the projects list can be sorted by (mirrors the backend sort allow-list). */
type ProjectSortKey =
  | 'number'
  | 'customerName'
  | 'name'
  | 'categoryName'
  | 'projectManagerName'
  | 'startDate'
  | 'endDate'
  | 'budgetHours'
  | 'actualHours';

type ProjectSort = { key: ProjectSortKey; dir: SortDirection };

/**
 * Badge tone for a project end date: red once past due, amber within the next 30 days,
 * otherwise no badge (plain text).
 */
function endDateTone(endDate: string | null): 'amber' | 'red' | null {
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(`${endDate}T00:00:00`);
  const diffDays = Math.floor((end.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0) return 'red';
  if (diffDays <= 30) return 'amber';
  return null;
}

/** Percent of budget hours used (actual ÷ budget × 100), or null when budget is unset/zero. */
function budgetPercent(budgetHours: string, actualHours: string): number | null {
  const budget = Number(budgetHours);
  const actual = Number(actualHours);
  if (!Number.isFinite(budget) || budget <= 0) return null;
  return (actual / budget) * 100;
}

/** Badge tone for budget usage: red at 100%+, amber from 90–99%, otherwise no badge. */
function budgetPercentTone(pct: number): 'amber' | 'red' | null {
  if (pct >= 100) return 'red';
  if (pct >= 90) return 'amber';
  return null;
}

/** A clickable table header that toggles server-side sorting for its column. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  align = 'left',
}: {
  label: string;
  sortKey: ProjectSortKey;
  sort: ProjectSort;
  onToggle: (key: ProjectSortKey) => void;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = sort.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          active ? 'text-slate-700' : 'hover:text-slate-700'
        }`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

const INLINE_TRIGGER_CLASS =
  'text-left text-slate-600 underline decoration-dotted decoration-slate-300 underline-offset-2 hover:text-brand-700 disabled:no-underline';
const INLINE_CONTROL_CLASS =
  'w-full rounded border border-slate-300 px-1 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

/** Inline project-manager editor: click the name to pick a different active manager. */
function ManagerCell({
  project,
  canEdit,
  managers,
  onSave,
  saving,
}: {
  project: Project;
  canEdit: boolean;
  managers: AssignableMember[];
  onSave: (id: string, input: UpdateProjectInput) => void;
  saving: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const label = project.projectManagerName || '—';
  if (!canEdit) return <span className="text-slate-600">{label}</span>;
  if (editing) {
    // Ensure the project's current manager is selectable even if no longer flagged as a PM.
    const options = managers.some((m) => m.id === project.projectManagerId)
      ? managers
      : [
          { id: project.projectManagerId, displayName: project.projectManagerName },
          ...managers,
        ];
    return (
      <select
        autoFocus
        defaultValue={project.projectManagerId}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => setEditing(false)}
        onChange={(e) => {
          const value = e.target.value;
          if (value && value !== project.projectManagerId) {
            onSave(project.id, { projectManagerId: value });
          }
          setEditing(false);
        }}
        className={INLINE_CONTROL_CLASS}
      >
        {options.map((m) => (
          <option key={m.id} value={m.id}>
            {m.displayName}
          </option>
        ))}
      </select>
    );
  }
  return (
    <button
      type="button"
      disabled={saving}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={INLINE_TRIGGER_CLASS}
    >
      {label}
    </button>
  );
}

/** Inline start/end date editor: click the date to change it (end date shows a due badge). */
function DateCell({
  project,
  field,
  canEdit,
  onSave,
  saving,
}: {
  project: Project;
  field: 'startDate' | 'endDate';
  canEdit: boolean;
  onSave: (id: string, input: UpdateProjectInput) => void;
  saving: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const value = field === 'startDate' ? project.startDate : project.endDate;
  const tone = field === 'endDate' ? endDateTone(project.endDate) : null;

  if (editing && canEdit) {
    return (
      <input
        type="date"
        autoFocus
        defaultValue={value ?? ''}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        onBlur={(e) => {
          const next = e.target.value ? e.target.value : null;
          if (next !== (value ?? null)) {
            onSave(project.id, field === 'startDate' ? { startDate: next } : { endDate: next });
          }
          setEditing(false);
        }}
        className={INLINE_CONTROL_CLASS}
      />
    );
  }

  const display =
    value && tone ? <Badge tone={tone}>{formatDate(value)}</Badge> : <>{formatDate(value)}</>;
  if (!canEdit) return <span className="text-slate-600">{display}</span>;
  return (
    <button
      type="button"
      disabled={saving}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={INLINE_TRIGGER_CLASS}
    >
      {display}
    </button>
  );
}

export function ProjectsPage(): JSX.Element {
  const { can, canSeeMenu, principal } = useAuth();
  const canEdit = can('projects', 'edit');
  const rowLink = useRowLink();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ProjectStatus | ''>('active');
  const [serviceCategoryId, setServiceCategoryId] = useState('');
  const [projectManagerIds, setProjectManagerIds] = useState<string[]>([]);
  const [assignedMemberIds, setAssignedMemberIds] = useState<string[]>([]);
  const [myProjects, setMyProjects] = useState(false);
  const [sort, setSort] = useState<ProjectSort>({ key: 'number', dir: 'asc' });

  const filterDefaults = useFilterDefaults('projects', (saved) => {
    const savedStatus = savedString(saved, 'status');
    if (savedStatus !== null) setStatus(savedStatus as ProjectStatus | '');
    const savedCategory = savedString(saved, 'serviceCategoryId');
    if (savedCategory !== null) setServiceCategoryId(savedCategory);
    const savedManagers = savedStringArray(saved, 'projectManagerIds');
    if (savedManagers !== null) setProjectManagerIds(savedManagers);
    const savedAssigned = savedStringArray(saved, 'assignedMemberIds');
    if (savedAssigned !== null) setAssignedMemberIds(savedAssigned);
    const savedMine = savedBoolean(saved, 'myProjects');
    if (savedMine !== null) setMyProjects(savedMine);
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const memberId = principal?.type === 'member' ? principal.id : null;

  const categoryOptions = useServiceCategoryOptions();
  const managerOptions = useProjectManagers();
  const managers = managerOptions.data ?? [];
  const updateProject = useUpdateProject();
  const quickSave = (id: string, input: UpdateProjectInput): void => {
    updateProject.mutate({ id, input });
  };

  const query = useProjects({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
    status: status || undefined,
    serviceCategoryId: serviceCategoryId || undefined,
    projectManagerIds: projectManagerIds.length > 0 ? projectManagerIds : undefined,
    assignedMemberIds: assignedMemberIds.length > 0 ? assignedMemberIds : undefined,
    assignedToMemberId: myProjects && memberId ? memberId : undefined,
    sortBy: sort.key,
    sortDir: sort.dir,
  });

  const projects = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const toggleSort = (key: ProjectSortKey): void => {
    setPage(1);
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const handleSearchSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleStatusChange = (value: string): void => {
    setPage(1);
    setStatus(value as ProjectStatus | '');
  };

  const handleCategoryChange = (value: string): void => {
    setPage(1);
    setServiceCategoryId(value);
  };

  const handleManagerChange = (next: string[]): void => {
    setPage(1);
    setProjectManagerIds(next);
  };

  const handleAssignedChange = (next: string[]): void => {
    setPage(1);
    setAssignedMemberIds(next);
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Projects"
        description="Track engagements, phases, and billable work for each customer."
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="projects" className="h-4 w-4" />
              New project
            </Button>
          ) : undefined
        }
      />

      {canSeeMenu('forecast') ? (
        <ProjectForecastPanel enabled={can('projects')} defaultCollapsed />
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <form onSubmit={handleSearchSubmit} className="flex max-w-md flex-1 items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by project name, customer, or number…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            {PROJECT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {PROJECT_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
          <select
            value={serviceCategoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All categories</option>
            {(categoryOptions.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <MemberMultiFilter
            selected={projectManagerIds}
            onChange={handleManagerChange}
            allLabel="All PM's"
          />
          <MemberMultiFilter
            selected={assignedMemberIds}
            onChange={handleAssignedChange}
            allLabel="All assigned members"
          />
          {memberId ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={myProjects}
                onChange={(e) => {
                  setPage(1);
                  setMyProjects(e.target.checked);
                }}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              My projects
            </label>
          ) : null}
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({
                status,
                serviceCategoryId,
                projectManagerIds,
                assignedMemberIds,
                myProjects,
              })
            }
          />
        </div>

        {updateProject.isError ? (
          <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            Could not save the change. Please try again.
          </div>
        ) : null}

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortableHeader label="Number" sortKey="number" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Customer"
                sortKey="customerName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader label="Name" sortKey="name" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Category"
                sortKey="categoryName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Project Manager"
                sortKey="projectManagerName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Start Date"
                sortKey="startDate"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="End Date"
                sortKey="endDate"
                sort={sort}
                onToggle={toggleSort}
              />
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Team</th>
              <SortableHeader
                label="Budget Hours"
                sortKey="budgetHours"
                sort={sort}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="Actual Hours"
                sortKey="actualHours"
                sort={sort}
                onToggle={toggleSort}
                align="right"
              />
              <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">Budget %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={11}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={projects.length === 0}
              emptyTitle="No projects found"
              emptyDescription={
                search ||
                status ||
                serviceCategoryId ||
                projectManagerIds.length > 0 ||
                assignedMemberIds.length > 0 ||
                myProjects
                  ? 'Try a different search or filter.'
                  : 'Create your first project to get started.'
              }
              emptyAction={
                canEdit &&
                !search &&
                !status &&
                !serviceCategoryId &&
                projectManagerIds.length === 0 &&
                assignedMemberIds.length === 0 &&
                !myProjects ? (
                  <Button onClick={openCreate}>New project</Button>
                ) : undefined
              }
            >
              {projects.map((project) => {
                const pct = budgetPercent(project.budgetHours, project.actualHours);
                const pctTone = pct === null ? null : budgetPercentTone(pct);
                return (
                  <tr
                    key={project.id}
                    className="cursor-pointer hover:bg-slate-50"
                    {...rowLink(`/projects/${project.id}`)}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">
                      #{project.number}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{project.customerName || '—'}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{project.name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {project.defaultServiceCategoryName || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <ManagerCell
                        project={project}
                        canEdit={canEdit}
                        managers={managers}
                        onSave={quickSave}
                        saving={updateProject.isPending}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <DateCell
                        project={project}
                        field="startDate"
                        canEdit={canEdit}
                        onSave={quickSave}
                        saving={updateProject.isPending}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <DateCell
                        project={project}
                        field="endDate"
                        canEdit={canEdit}
                        onSave={quickSave}
                        saving={updateProject.isPending}
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {project.teamMembers.length > 0 ? (
                        <span
                          className="line-clamp-2 max-w-[16rem] text-xs"
                          title={project.teamMembers.map((m) => m.displayName).join(', ')}
                        >
                          {project.teamMembers.map((m) => m.displayName).join(', ')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {project.budgetHours}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {project.actualHours}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {pct === null ? (
                        '—'
                      ) : pctTone ? (
                        <Badge tone={pctTone}>{`${pct.toFixed(0)}%`}</Badge>
                      ) : (
                        `${pct.toFixed(0)}%`
                      )}
                    </td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>

        {pageInfo && projects.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <ProjectFormModal isOpen={formOpen} project={editing} onClose={() => setFormOpen(false)} />
      <DetailOverlay listPath="/projects" />
    </div>
  );
}
